import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { vouchersApi } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { AuditTimeline } from '../../components/shared/AuditTimeline';
import { VoucherPrintTemplate } from '../../components/voucher/VoucherPrintTemplate';
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  getPaymentMethodLabel,
  getPaymentTypeLabel,
} from '../../lib/utils';
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  FileText,
  Download,
  Building2,
  User,
  Calendar,
  CreditCard,
  Banknote,
  Printer,
  PauseCircle,
  PlayCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';

export const ManagerReviewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [voucher, setVoucher] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [actionDialog, setActionDialog] = useState({ open: false, type: null });
  const [note, setNote] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [voucherData, logsData] = await Promise.all([
          vouchersApi.getById(id),
          vouchersApi.getAuditLogs(id),
        ]);
        setVoucher(voucherData);
        setAuditLogs(logsData);
      } catch (error) {
        toast.error('Voucher not found');
        navigate('/approvals');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, navigate]);

  const handleAction = async () => {
    setProcessing(true);
    try {
      let updated;
      const isAdditionalManagerAction = voucher.status === 'PENDING_ADDITIONAL_APPROVAL' && voucher.additionalManagerId === user?.id;
      
      if (actionDialog.type === 'approve') {
        if (isAdditionalManagerAction) {
          updated = await vouchersApi.additionalManagerApprove(id, note);
          toast.success('Voucher approved by additional manager - sent to Internal Audit');
        } else {
          updated = await vouchersApi.managerApprove(id, note);
          if (updated.status === 'PENDING_ADDITIONAL_APPROVAL') {
            toast.success('Voucher approved - sent to additional manager for second approval');
          } else {
            toast.success('Voucher approved - sent to Internal Audit');
          }
        }
      } else if (actionDialog.type === 'hold') {
        updated = await vouchersApi.holdVoucher(id, note);
        toast.success('Voucher has been put on hold');
      } else if (actionDialog.type === 'release') {
        updated = await vouchersApi.releaseHold(id, note);
        toast.success('Voucher has been released from hold');
      } else {
        if (isAdditionalManagerAction) {
          updated = await vouchersApi.additionalManagerReject(id, note);
          toast.success('Voucher rejected by additional manager');
        } else {
          updated = await vouchersApi.managerReject(id, note);
          toast.success('Voucher rejected and returned to employee');
        }
      }
      setVoucher(updated);
      const logs = await vouchersApi.getAuditLogs(id);
      setAuditLogs(logs);
      setActionDialog({ open: false, type: null });
      setNote('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process voucher');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-48 bg-gray-100 rounded animate-pulse" />
            ))}
          </div>
          <div className="h-96 bg-gray-100 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  if (!voucher) return null;

  const attachments = voucher.attachments?.map((a) => {
    const [filename, originalName] = a.split('|');
    return { filename, originalName: originalName || filename };
  }) || [];

  // Cannot take action if user is the creator of the voucher
  const isCreator = voucher.createdById === user?.id;
  const isAssignedManager = voucher.assignedManagerId === user?.id;
  const isAdditionalManager = voucher.additionalManagerId === user?.id;
  
  // Can take action as first manager on SUBMITTED status
  const canTakeFirstManagerAction = voucher.status === 'SUBMITTED' && isAssignedManager && !isCreator;
  // Can take action as additional manager on PENDING_ADDITIONAL_APPROVAL status
  const canTakeAdditionalManagerAction = voucher.status === 'PENDING_ADDITIONAL_APPROVAL' && isAdditionalManager && !isCreator;
  
  const canTakeAction = canTakeFirstManagerAction || canTakeAdditionalManagerAction;
  const isAdditionalApproval = canTakeAdditionalManagerAction;

  return (
    <div className="space-y-6" data-testid="manager-review-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/approvals')}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-heading font-semibold tracking-tight">
                Review Voucher
              </h1>
              <StatusBadge status={voucher.status} />
            </div>
            <p className="text-muted-foreground text-sm mt-1">
              Submitted by {voucher.createdByName} on {formatDateTime(voucher.submittedAt)}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 print:hidden">
          <Button
            variant="outline"
            onClick={() => window.print()}
            data-testid="print-button"
          >
            <Printer className="w-4 h-4 mr-2" /> Print
          </Button>
          {canTakeAction && (
            <>
              <Button
                variant="outline"
                onClick={() => setActionDialog({ open: true, type: 'reject' })}
                className="border-red-200 text-red-600 hover:bg-red-50"
                data-testid="reject-button"
              >
                <XCircle className="w-4 h-4 mr-2" /> Reject
              </Button>
              <Button
                variant="outline"
                onClick={() => setActionDialog({ open: true, type: 'hold' })}
                className="border-yellow-300 text-yellow-700 hover:bg-yellow-50"
                data-testid="hold-button"
              >
                <PauseCircle className="w-4 h-4 mr-2" /> Hold
              </Button>
              <Button
                onClick={() => setActionDialog({ open: true, type: 'approve' })}
                className="bg-green-600 hover:bg-green-700"
                data-testid="approve-button"
            >
              <CheckCircle className="w-4 h-4 mr-2" /> Approve
            </Button>
            </>
          )}
          {voucher.status === 'HOLD' && (
            <Button
              onClick={() => setActionDialog({ open: true, type: 'release' })}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="release-hold-button"
            >
              <PlayCircle className="w-4 h-4 mr-2" /> Release Hold
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Employee Info */}
          <Card className="border-l-4 border-l-[#0047AB]">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#6a5da4]/10 flex items-center justify-center">
                  <User className="w-6 h-6 text-[#6a5da4]" />
                </div>
                <div>
                  <p className="font-semibold">{voucher.createdByName}</p>
                  <p className="text-sm text-muted-foreground">
                    {voucher.department} • {voucher.branch}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Voucher Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Date</p>
                    <p className="font-medium">{formatDate(voucher.date)}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Building2 className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Branch</p>
                    <p className="font-medium">{voucher.branch}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Building2 className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Department</p>
                    <p className="font-medium">{voucher.department}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Beneficiary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Beneficiary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Name</p>
                  <p className="font-medium">{voucher.beneficiaryName}</p>
                </div>
                {voucher.beneficiaryAddress && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Address</p>
                    <p className="font-medium">{voucher.beneficiaryAddress}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Payment Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Payment Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="flex items-start gap-3">
                  <CreditCard className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Method</p>
                    <p className="font-medium">{getPaymentMethodLabel(voucher.paymentMethod)}</p>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Type</p>
                  <p className="font-medium">{getPaymentTypeLabel(voucher.paymentType)}</p>
                </div>
                {(voucher.advancePercent !== null && voucher.advancePercent !== undefined) && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Advance %</p>
                    <p className="font-medium">{voucher.advancePercent}%</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Currency</p>
                  <p className="font-medium">{voucher.currency}</p>
                </div>
              </div>

              {voucher.paymentMethod === 'TRANSFER' && (
                <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Bank Details</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Bank Name</p>
                      <p className="font-medium">{voucher.bankName}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">IBAN</p>
                      <p className="font-mono text-sm">{voucher.iban}</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Line Items</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Document No.</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Invoice Amount</TableHead>
                    <TableHead className="text-right">Payable Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {voucher.items?.map((item, index) => (
                    <TableRow key={item.id || index}>
                      <TableCell className="font-mono">{item.invoiceNo || '-'}</TableCell>
                      <TableCell className="text-muted-foreground">{item.note || '-'}</TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(item.amount, item.currency)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(item.payableAmount || 0, item.currency)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-gray-50 font-semibold">
                    <TableCell colSpan={3}>Total Payable Amount</TableCell>
                    <TableCell className="text-right font-mono text-lg text-[#6a5da4]">
                      {formatCurrency(voucher.amountTotal, voucher.currency)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Attachments */}
          {attachments.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-heading">Attachments</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {attachments.map((attachment) => (
                    <div
                      key={attachment.filename}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-muted-foreground" />
                        <span className="text-sm">{attachment.originalName}</span>
                      </div>
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/${voucher.id}/${attachment.filename}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#6a5da4] hover:underline text-sm flex items-center gap-1"
                      >
                        <Download className="w-4 h-4" /> Download
                      </a>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Additional Attachments */}
          {voucher.additionalAttachments?.length > 0 && (
            <Card className="border-blue-200">
              <CardHeader>
                <CardTitle className="text-lg font-heading text-blue-700">Additional Attachments</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {voucher.additionalAttachments.map((attachment, index) => {
                    const parts = attachment.split('|');
                    const filename = parts[0];
                    const originalName = parts[1] || filename;
                    const uploadedAt = parts[2];
                    return (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-100"
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-blue-600" />
                          <div>
                            <span className="text-sm font-medium">{originalName}</span>
                            {uploadedAt && (
                              <p className="text-xs text-muted-foreground">
                                Uploaded: {formatDateTime(uploadedAt)}
                              </p>
                            )}
                          </div>
                        </div>
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/${voucher.id}/additional/${filename}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline text-sm flex items-center gap-1"
                        >
                          <Download className="w-4 h-4" /> Download
                        </a>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Amount Summary */}
          <Card className="bg-[#6a5da4] text-white">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-4">
                <Banknote className="w-8 h-8 opacity-80" />
                <div>
                  <p className="text-sm opacity-80">Total Amount</p>
                  <p className="text-3xl font-mono font-semibold">
                    {formatCurrency(voucher.amountTotal, voucher.currency)}
                  </p>
                </div>
              </div>
              {voucher.amountWords && (
                <p className="text-sm opacity-80 pt-4 border-t border-white/20">
                  {voucher.amountWords}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Audit Log */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Activity Log</CardTitle>
            </CardHeader>
            <CardContent>
              <AuditTimeline logs={auditLogs} />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Action Dialog */}
      <Dialog open={actionDialog.open} onOpenChange={(open) => setActionDialog({ open, type: actionDialog.type })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionDialog.type === 'approve' ? 'Approve Voucher' : 
               actionDialog.type === 'hold' ? 'Put Voucher on Hold' :
               actionDialog.type === 'release' ? 'Release Voucher from Hold' : 'Reject Voucher'}
            </DialogTitle>
            <DialogDescription>
              {actionDialog.type === 'approve'
                ? 'This voucher will be forwarded to the accounts department for processing.'
                : actionDialog.type === 'hold'
                ? 'This voucher will be put on hold and the employee will be notified.'
                : actionDialog.type === 'release'
                ? 'This voucher will be released from hold and resume its previous workflow.'
                : 'This voucher will be returned to the employee for corrections.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="note">
                {actionDialog.type === 'approve' ? 'Note (optional)' : 
                 actionDialog.type === 'hold' ? 'Hold Reason' :
                 actionDialog.type === 'release' ? 'Release Note (optional)' : 'Rejection Reason'}
              </Label>
              <Textarea
                id="note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={
                  actionDialog.type === 'approve'
                    ? 'Add any comments for the accounts team...'
                    : actionDialog.type === 'hold'
                    ? 'Please explain why this voucher is being put on hold...'
                    : actionDialog.type === 'release'
                    ? 'Add any comments about the release...'
                    : 'Please explain why this voucher is being rejected...'
                }
                rows={3}
                data-testid="action-note"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setActionDialog({ open: false, type: null });
                setNote('');
              }}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAction}
              disabled={processing || ((actionDialog.type === 'reject' || actionDialog.type === 'hold') && !note)}
              className={
                actionDialog.type === 'approve' ? 'bg-green-600 hover:bg-green-700' : 
                actionDialog.type === 'hold' ? 'bg-yellow-500 hover:bg-yellow-600' :
                actionDialog.type === 'release' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-red-600 hover:bg-red-700'
              }
              data-testid="confirm-action"
            >
              {processing ? 'Processing...' : 
               actionDialog.type === 'approve' ? 'Approve' : 
               actionDialog.type === 'hold' ? 'Put on Hold' :
               actionDialog.type === 'release' ? 'Release' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print Template - Hidden on screen, visible on print */}
      <VoucherPrintTemplate voucher={voucher} />
    </div>
  );
};

export default ManagerReviewPage;
